import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { DOMAIN_LANGUAGE, NL } from '../../../constants/browser';

import { Blurred, Link, PayNowButton } from '../../common';
import {
  INSTALLMENT_VALUE,
  OFFLINE_SEPA_VALUE,
  SEPA_DIRECT_DEBIT_VALUE,
  SEPA_IMAGE
} from '../../../constants/payment';
import * as routes from '../../../constants/routes';

import { filterPaymentMethods } from '../../../utils/payment';

export const HOME_PAY_NOW_LINK = 'page.home.component.pay-now.pay-now-link';

const parseMethods = (paymentMethods, paymentHide, balance) => {
  if (paymentHide) {
    return paymentMethods.slice(0, 1);
  } else {
    const firstSepaValueFromOrder = [
      OFFLINE_SEPA_VALUE,
      INSTALLMENT_VALUE,
      SEPA_DIRECT_DEBIT_VALUE
    ].reduce((acc, sepaValue) => {
      if (!acc.found) {
        const method = paymentMethods.find(({ value }) => value === sepaValue);
        if (method) {
          acc = method;
          acc.found = true;
        }
      }
      return acc;
    }, {});
    let methods = paymentMethods.reduce((acc, method) => {
      if (method.image !== SEPA_IMAGE) {
        acc.push(method);
      } else if (method.value === firstSepaValueFromOrder.value) {
        const { found, ...sepaMethod } = firstSepaValueFromOrder;
        acc.push(sepaMethod);
      }
      return acc;
    }, []);
    return filterPaymentMethods(methods, { balance });
  }
};

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 136,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px 24px 12px 24px',
    background: theme.palette.background.default,
    boxSizing: 'border-box'
  },
  rootMobile: {
    minHeight: 118,
    padding: '24px 16px 8px 16px'
  },
  payNowMobile: {
    width: '100%',
    maxWidth: 256
  },
  methods: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center'
  },
  methodsMobile: {
    marginTop: 16,
    width: '100%',
    justifyContent: 'space-evenly'
  },
  methodsMobileNL: {
    width: '70%'
  },
  link: {
    display: 'inline-flex',
    margin: 0,
    padding: 12
  },
  linkNL: {
    padding: '4px 12px'
  },
  linkMobile: {
    padding: '2px 0 12px 0'
  },
  logo: {
    height: 24
  },
  logoNL: {
    height: 40
  },
  logoMobile: {
    height: 16
  },
  logoMobileNL: {
    height: 28
  }
}));

const Payment = ({
  isMobileSize,
  balance,
  paymentMethods,
  paymentHide,
  blur
}) => {
  const classes = useStyles();
  let methods = parseMethods(paymentMethods, paymentHide, balance);

  const isNL = DOMAIN_LANGUAGE === NL;
  return (
    <Box
      className={classNames(classes.root, isMobileSize && classes.rootMobile)}
    >
      <Blurred blur={blur}>
        <PayNowButton
          id={HOME_PAY_NOW_LINK}
          className={isMobileSize ? classes.payNowMobile : null}
        />
      </Blurred>
      <Box
        className={classNames(
          classes.methods,
          isMobileSize && classes.methodsMobile,
          isNL && isMobileSize && classes.methodsMobileNL
        )}
      >
        {methods.length > 0 &&
          methods.map(({ label, image, value, id }) => (
            <Blurred blur={blur} key={id}>
              <Link
                className={classNames(
                  classes.link,
                  isNL && classes.linkNL,
                  isMobileSize && classes.linkMobile
                )}
                id={`page.home.component.payment-list.${id}`}
                to={`${routes.PAYMENTS}?method=${value}`}
              >
                <img
                  className={classNames(
                    isNL ? classes.logoNL : classes.logo,
                    isMobileSize && classes.logoMobile,
                    isMobileSize && isNL && classes.logoMobileNL
                  )}
                  src={image}
                  alt={`${label}-logo`}
                />
              </Link>
            </Blurred>
          ))}
      </Box>
    </Box>
  );
};

Payment.propTypes = {
  isMobileSize: PropTypes.bool,
  paymentMethods: PropTypes.array,
  paymentHide: PropTypes.bool,
  balance: PropTypes.number,
  blur: PropTypes.bool
};

export default Payment;
